import { useState } from 'react';
import { reportsApi } from '../../api/services';
import type { AgingReportDto, AgingRowDto, StatementDto } from '../../api/types';
import { Alert, Button, Field, Input, Loadable, Table, Toolbar, fmtDate, fmtMoney, money, todayIso, useAction, useAsync } from '../../ui/components';

export function AgingView({ load, csvReport }: { load: (asOf?: string) => Promise<AgingReportDto>; csvReport: 'receivables-aging' | 'payables-aging' }) {
  const [asOf, setAsOf] = useState(todayIso());
  const state = useAsync(() => load(asOf), [asOf]);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar>
        <Field label="Al"><Input type="date" value={asOf} onChange={setAsOf} /></Field>
        <Button busy={busy} onClick={() => run(() => reportsApi.csv(csvReport, { asOf }, `${csvReport}.csv`))}>Exportar CSV</Button>
      </Toolbar>
      <Loadable state={state}>
        {(r) => (
          <>
            <Alert>{r.basis === 'DUE_DATE' ? 'Antigüedad por fecha de vencimiento.' : 'Antigüedad desde la fecha de emisión (no hay vencimientos registrados).'} {r.currencyNote}</Alert>
            <Table rows={r.rows} rowKey={(x) => x.partyCode} columns={[
              { key: 'c', title: 'Código', render: (x) => x.partyCode },
              { key: 'n', title: 'Nombre', render: (x) => x.partyName ?? '' },
              money<AgingRowDto>('a', 'Al día', (x) => x.current),
              money<AgingRowDto>('b', '1-30', (x) => x.days1To30),
              money<AgingRowDto>('c3', '31-60', (x) => x.days31To60),
              money<AgingRowDto>('d', '61-90', (x) => x.days61To90),
              money<AgingRowDto>('e', 'Más de 90', (x) => x.over90),
              money<AgingRowDto>('t', 'Total', (x) => x.total),
            ]} footer={
              <tr><td colSpan={2}><strong>Totales</strong></td>
                {[r.totals.current, r.totals.days1To30, r.totals.days31To60, r.totals.days61To90, r.totals.over90, r.totals.total].map((v, i) => <td key={i} style={{ textAlign: 'right' }}><strong>{fmtMoney(v)}</strong></td>)}</tr>
            } />
          </>
        )}
      </Loadable>
    </>
  );
}

export function StatementView({ load }: { load: (from?: string, to?: string) => Promise<StatementDto> }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(todayIso());
  const state = useAsync(() => load(from || undefined, to || undefined), [from, to]);
  return (
    <>
      <Toolbar>
        <Field label="Desde"><Input type="date" value={from} onChange={setFrom} /></Field>
        <Field label="Hasta"><Input type="date" value={to} onChange={setTo} /></Field>
      </Toolbar>
      <Loadable state={state}>
        {(s) => (
          <>
            <p>Saldo inicial: <strong>{fmtMoney(s.openingBalance)}</strong> · Saldo final: <strong>{fmtMoney(s.closingBalance)}</strong></p>
            <Table rows={s.lines.map((l, i) => ({ ...l, i }))} rowKey={(l) => l.i} columns={[
              { key: 'f', title: 'Fecha', render: (l) => fmtDate(l.date) },
              { key: 't', title: 'Tipo', render: (l) => l.type },
              { key: 'd', title: 'Documento', render: (l) => l.document },
              { key: 'x', title: 'Detalle', render: (l) => l.description ?? '' },
              { key: 'c', title: 'Cargo', align: 'right', render: (l) => fmtMoney(l.charge) },
              { key: 'a', title: 'Abono', align: 'right', render: (l) => fmtMoney(l.credit) },
              { key: 's', title: 'Saldo', align: 'right', render: (l) => fmtMoney(l.balance) },
              { key: 'e', title: 'Asiento', render: (l) => l.entryCode ?? '' },
            ]} />
          </>
        )}
      </Loadable>
    </>
  );
}
